import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Section } from "./ui/section";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Avatar } from "./ui/avatar";
import { formatRole } from "../lib/groupRole";
import {
  useAddParticipant,
  useRemoveParticipant,
  useUpdateParticipant,
} from "../api/hooks";
import type { Group, Participant } from "../api/types";

type Role = Participant["role"];

const ROLES: Role[] = ["OWNER", "MEMBER", "VIEWER"];

interface ParticipantsManagerProps {
  group: Group;
  canManage: boolean;
  adding: boolean;
  onAddingChange: (adding: boolean) => void;
}

export function ParticipantsManager({
  group,
  canManage,
  adding,
  onAddingChange,
}: ParticipantsManagerProps): ReactNode {
  const [editing, setEditing] = useState<Participant | null>(null);
  const [removing, setRemoving] = useState<Participant | null>(null);

  return (
    <Section
      title="Participants"
      description="People who can be assigned expenses and splits in this group."
      contentClassName="space-y-stack-sm"
    >
      {group.participants.map((p) => (
        <div
          key={p.id}
          className="flex items-center gap-3 rounded-md border border-outline-variant/60 p-4"
        >
          <Avatar name={p.displayName} size="md" />
          {/*
            Opens what this participant is involved in *inside this group*
            (issue #448) — which works for a guest too, unlike an account
            profile. That page links on to the account profile (issue #452) when
            there is one.
          */}
          <Link
            to={`/groups/${group.id}/participants/${p.id}`}
            className="min-w-0 flex-1 rounded-sm transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <p className="truncate text-body-md font-bold">{p.displayName}</p>
            <p className="text-label-sm text-on-surface-variant">
              {formatRole(p.role)}
              {p.type === "GUEST" ? " · Guest" : ""}
            </p>
          </Link>
          {canManage ? (
            <>
              <Button
                size="icon"
                variant="ghost"
                aria-label={`Edit ${p.displayName}`}
                onClick={() => setEditing(p)}
              >
                <Pencil className="size-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                aria-label={`Remove ${p.displayName}`}
                onClick={() => setRemoving(p)}
              >
                <Trash2 className="size-4" />
              </Button>
            </>
          ) : null}
        </div>
      ))}
      {group.participants.length === 0 ? (
        <p className="py-6 text-center text-body-md text-on-surface-variant">
          No participants yet.
        </p>
      ) : null}

      <AddParticipantDialog
        group={group}
        open={adding}
        onOpenChange={onAddingChange}
      />
      <EditParticipantDialog
        group={group}
        participant={editing}
        onClose={() => setEditing(null)}
      />
      <RemoveParticipantDialog
        group={group}
        participant={removing}
        onClose={() => setRemoving(null)}
      />
    </Section>
  );
}

function AddParticipantDialog({
  group,
  open,
  onOpenChange,
}: {
  group: Group;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): ReactNode {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {/* Mounting the body conditionally lets useState seed defaults each
            time the dialog opens — cancelling a half-typed entry doesn't
            leak draft text or a stale error into the next opening. */}
        {open ? (
          <AddParticipantBody groupId={group.id} onOpenChange={onOpenChange} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function AddParticipantBody({
  groupId,
  onOpenChange,
}: {
  groupId: string;
  onOpenChange: (open: boolean) => void;
}): ReactNode {
  const addParticipant = useAddParticipant(groupId);
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<Role>("MEMBER");

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!displayName) return;
    try {
      await addParticipant.mutateAsync({
        type: "GUEST",
        displayName,
        role,
      });
      onOpenChange(false);
    } catch {
      // The mutation error is rendered below; keep the dialog open to retry.
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Add a participant</DialogTitle>
        <DialogDescription>
          Adds a guest. Share a link with them later to let them claim the slot
          with their Tailspend account.
        </DialogDescription>
      </DialogHeader>
      <form className="space-y-5" onSubmit={onSubmit}>
        <div className="space-y-3">
          <Label htmlFor="participant-name">Name</Label>
          <Input
            id="participant-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            required
            minLength={1}
            maxLength={64}
            placeholder="Sam"
          />
        </div>
        <div className="space-y-3">
          <Label htmlFor="participant-role">Role</Label>
          <RoleSelect id="participant-role" value={role} onChange={setRole} />
        </div>
        {addParticipant.error ? (
          <p className="text-label-md text-error">Couldn't add participant.</p>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!displayName || addParticipant.isPending}
          >
            <Plus className="size-4" />
            {addParticipant.isPending ? "Adding…" : "Add"}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}

function EditParticipantDialog({
  group,
  participant,
  onClose,
}: {
  group: Group;
  participant: Participant | null;
  onClose: () => void;
}): ReactNode {
  return (
    <Dialog
      open={participant !== null}
      onOpenChange={(o) => (!o ? onClose() : null)}
    >
      <DialogContent>
        {participant ? (
          <EditParticipantBody
            groupId={group.id}
            participant={participant}
            onClose={onClose}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function EditParticipantBody({
  groupId,
  participant,
  onClose,
}: {
  groupId: string;
  participant: Participant;
  onClose: () => void;
}): ReactNode {
  const updateParticipant = useUpdateParticipant(groupId);
  const [displayName, setDisplayName] = useState(participant.displayName);
  const [role, setRole] = useState<Role>(participant.role);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const patch: { displayName?: string; role?: Role } = {};
    if (displayName !== participant.displayName)
      patch.displayName = displayName;
    if (role !== participant.role) patch.role = role;
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    try {
      await updateParticipant.mutateAsync({
        participantId: participant.id,
        body: patch,
      });
      onClose();
    } catch {
      // The mutation error is rendered below; keep the dialog open to retry.
    }
  }

  const dirty =
    displayName !== participant.displayName || role !== participant.role;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit participant</DialogTitle>
      </DialogHeader>
      <form className="space-y-5" onSubmit={onSubmit}>
        {participant.type === "GUEST" ? (
          <div className="space-y-3">
            <Label htmlFor="edit-participant-name">Name</Label>
            <Input
              id="edit-participant-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              minLength={1}
              maxLength={64}
            />
          </div>
        ) : null}
        <div className="space-y-3">
          <Label htmlFor="edit-participant-role">Role</Label>
          <RoleSelect
            id="edit-participant-role"
            value={role}
            onChange={setRole}
          />
        </div>
        {updateParticipant.error ? (
          <p className="text-label-md text-error">Couldn't save changes.</p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!dirty || updateParticipant.isPending || !displayName}
          >
            {updateParticipant.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}

function RemoveParticipantDialog({
  group,
  participant,
  onClose,
}: {
  group: Group;
  participant: Participant | null;
  onClose: () => void;
}): ReactNode {
  return (
    <Dialog
      open={participant !== null}
      onOpenChange={(o) => (!o ? onClose() : null)}
    >
      <DialogContent>
        {/* The mutation hook lives inside the body so its error/pending
            state resets between participants — without this split, a failed
            remove of A would surface its error message on B's confirm. */}
        {participant ? (
          <RemoveParticipantBody
            group={group}
            participant={participant}
            onClose={onClose}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function RemoveParticipantBody({
  group,
  participant,
  onClose,
}: {
  group: Group;
  participant: Participant;
  onClose: () => void;
}): ReactNode {
  const removeParticipant = useRemoveParticipant(group.id);

  async function onConfirm(): Promise<void> {
    try {
      await removeParticipant.mutateAsync(participant.id);
      onClose();
    } catch {
      // The mutation error is rendered below; keep the dialog open to retry.
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Remove participant</DialogTitle>
        <DialogDescription>
          Remove {participant.displayName} from {group.label}? They'll lose
          access and their share of any open balances will need to be settled.
        </DialogDescription>
      </DialogHeader>
      {removeParticipant.error ? (
        <p className="text-label-md text-error">Couldn't remove participant.</p>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={onConfirm}
          disabled={removeParticipant.isPending}
        >
          {removeParticipant.isPending ? "Removing…" : "Remove"}
        </Button>
      </DialogFooter>
    </>
  );
}

function RoleSelect({
  id,
  value,
  onChange,
}: {
  id?: string;
  value: Role;
  onChange: (role: Role) => void;
}): ReactNode {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value as Role)}
      className="flex h-11 w-full rounded-sm bg-surface-container-low px-4 text-body-md text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {ROLES.map((r) => (
        <option key={r} value={r}>
          {formatRole(r)}
        </option>
      ))}
    </select>
  );
}
